import React from 'react';

import PropTypes from 'prop-types';
import { withRouter, Link } from 'react-router';
import partial from 'lodash/partial';
import cn from 'classnames';

import { eventFullPropTypeShape } from '../../../../proptypes';
import {
  getAvailableTicketPrices,
  racesHaveAvailableCharityTickets,
} from '../../../../utils/tickets';
import StarRating from '../../../../components/StarRating';
import EventLabel from './EventLabel/EventLabel';
import HeartButton from '../../../../containers/HeartButton';

import { srOnly } from '../../../../base-styles/mixins.module.css';

import {
  container,
  link,
  eventCard,
  column1,
  column2,
  column3,
  date,
  month,
  year,
  eventTitle,
  titleWithRegistrationPerks,
  eventInfo,
  distances,
  priceRating,
  price,
  eventImg,
  rating,
  starsWrapper,
  reviewsCounter,
  featured as featuredStyle,
  active as activeStyle,
  covidBadge,
} from './styles.module.css';

const getPriceLabel = (races = []) => {
  const ticketPrices = getAvailableTicketPrices(races);
  const formattedPrice = ticketPrices
    ? `${ticketPrices.fromFormatted}${
        ticketPrices.toFormatted ? ` – ${ticketPrices.toFormatted}` : ''
      }`
    : null;

  if (formattedPrice) {
    return formattedPrice;
  }

  const hasCharityTickets = racesHaveAvailableCharityTickets(races);
  if (hasCharityTickets) {
    return 'Charity places';
  }
};

const SearchResultCard = (props) => {
  const {
    eventId,
    startDate,
    startMonth,
    startDay,
    hasRegistrationPerks,
    place,
    thumbnailUrl,
    races,
    title,
    location,
    permalink,
    reviewSummary,
    countryCode,
    onClick,
    active,
    elementRef,
    onMouseEnter,
    onMouseLeave,
    showCovid19Badge,
  } = props;

  const bookingEnabled =
    races && races.bookingEnabled ? races.bookingEnabled : false;

  const priceLabel = races?.races ? getPriceLabel(races.races) : null;

  const titleContainerStyle = hasRegistrationPerks
    ? titleWithRegistrationPerks
    : eventTitle;

  const eventDate = new Date(startDate);
  const eventYear = eventDate.getFullYear();
  const startYear = eventYear > new Date().getFullYear() && eventYear;

  const metaDate =
    eventDate &&
    startDay &&
    `${eventDate.getFullYear()}-${eventDate.getMonth() + 1}-${startDay}`;

  let distanceArray = races.races
    .map((race) => race.information && race.information.metricLength)
    .filter((race) => !!race)
    .sort((a, b) => b - a);

  if (countryCode === 'GB' || countryCode === 'US') {
    distanceArray = distanceArray.map(
      (length) => `${(length * 0.621371).toFixed(1)} mi`,
    );
  } else {
    distanceArray = distanceArray.map((length) => `${length.toFixed(1)} km`);
  }

  const distanceString = [...new Set(distanceArray)].join(` • `);

  return (
    <li
      className={cn({
        [container]: true,
        [activeStyle]: active,
      })}
      onMouseEnter={partial(onMouseEnter, eventId)}
      onMouseLeave={onMouseLeave}
      data-testing-id="event-card"
      itemScope
      itemType="https://schema.org/Event"
      ref={elementRef}
    >
      <Link
        to={{ pathname: permalink }}
        itemProp="url"
        className={link}
        onClick={onClick}
        target="_blank"
      >
        <span className={srOnly}>{title}</span>
      </Link>
      <div className={eventCard}>
        <div className={column1} itemProp="startDate" content={metaDate}>
          <span className={date}>{startDay}</span>
          <span className={month}>{startMonth}</span>
          <span className={year}>{startYear}</span>
          <div className="save-icon">
            <HeartButton
              id={eventId}
              bookingPartner={races && races.bookingPartner}
              loginReturnUrl={location.pathname}
              trackingObject={{
                title,
                startDate,
                id: eventId,
                bookingEnabled,
              }}
            >
              <div className="heart" aria-hidden="true" />
            </HeartButton>
          </div>
        </div>
        <div className={column2}>
          <h3 className={titleContainerStyle} itemProp="name">
            {title}
          </h3>
          <div
            className={eventInfo}
            itemProp="location"
            itemType="https://schema.org/Place"
            itemScope
          >
            <div
              itemProp="address"
              itemScope
              itemType="http://schema.org/PostalAddress"
            >
              {/* Leave space before and after &#x2027; */}
              <EventLabel races={races.races} /> &#x2027;{' '}
              <span itemProp="addressLocality">
                {place && place.address && place.address.city}
              </span>
              <meta
                itemProp="addressRegion"
                content={place.address.stateLong}
              ></meta>
              <meta
                itemProp="addressCountry"
                content={place.address.countryLong}
              ></meta>
            </div>
          </div>
          <div className={distances}>{distanceString}</div>
          <div className={priceRating}>
            {priceLabel && <span className={price}>{priceLabel}</span>}
            {showCovid19Badge && <span className={covidBadge}>C-19 Plan</span>}
            <span className={rating}>
              {reviewSummary?.total > 0 && (
                <div className={rating}>
                  <span className={starsWrapper}>
                    <StarRating value={reviewSummary?.ratings?.overall} small />
                  </span>
                  <span className={reviewsCounter}>
                    {`(${reviewSummary.total})`}
                  </span>
                </div>
              )}
            </span>
          </div>
        </div>
        {thumbnailUrl && (
          <div className={column3}>
            <div
              style={{ backgroundImage: `url(${thumbnailUrl})` }}
              className={eventImg}
              itemProp="image"
              content={thumbnailUrl}
            >
              {eventId === 169829 && (
                <div className={featuredStyle}>FEATURED</div>
              )}
            </div>
          </div>
        )}
      </div>
    </li>
  );
};

SearchResultCard.propTypes = {
  /* ===== Functionaly Computed in Container ===== */
  /**
   * If the race has registration perks.
   */
  hasRegistrationPerks: PropTypes.bool,

  thumbnailUrl: PropTypes.string,

  /**
   * Start month represented with 3 letters. (i.e. JUN)
   */
  startMonth: PropTypes.string.isRequired,
  startDay: PropTypes.string.isRequired,
  /**
   * ISO string of event start date.
   */
  startDate: eventFullPropTypeShape.startDate.isRequired,

  /**
   * String title of the event.
   */
  title: PropTypes.string.isRequired,
  /**
   * Object of race data.
   */
  races: PropTypes.shape({
    races: PropTypes.arrayOf(
      PropTypes.shape({
        information: PropTypes.shape({
          sportLabel: PropTypes.string,
          metricLength: PropTypes.number,
        }),
      }),
    ).isRequired,
    available: PropTypes.bool,
    pricesFrom: PropTypes.number,
    pricesTo: PropTypes.number,
    currencyLabel: PropTypes.string,
  }),

  /**
   * String permalink to the event page.
   */
  permalink: PropTypes.string.isRequired,
};

SearchResultCard.defaultProps = {
  hasRegistrationPerks: false,
  races: undefined,
  thumbnailUrl: undefined,
};

export default withRouter(SearchResultCard);
